import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line
} from 'recharts';
import { RefreshCw } from 'lucide-react';

const formatCurrency = (n) => {
  if (!n && n !== 0) return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

export default function AnalyticsPage() {
  const { activePeriod } = usePeriod();
  const [dashData, setDashData] = useState(null);
  const [spendByPeriod, setSpendByPeriod] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        API.get('/analytics/dashboard', { params: activePeriod ? { period_id: activePeriod.id } : {} }),
        API.get('/analytics/spend-by-period'),
      ]);
      setDashData(d.data);
      setSpendByPeriod(s.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activePeriod?.id]);

  if (loading) return <div className="p-8 flex items-center justify-center"><RefreshCw size={24} className="animate-spin text-slate-400" /></div>;

  const kpis = dashData?.kpis || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Analytics</h1>
        <button onClick={load} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Spend',    value: formatCurrency(kpis.total_spend) },
          { label: 'Transactions',   value: kpis.total_transactions || 0 },
          { label: 'Coded %',        value: `${kpis.coded_pct || 0}%` },
          { label: 'Approved',       value: kpis.approved_count || 0 },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-lg border shadow-sm p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
          </div>
        ))}
      </div>

      {/* Spend by period (line chart) */}
      {spendByPeriod.length > 0 && (
        <div className="bg-white rounded-lg border shadow-sm p-5">
          <h3 className="text-sm font-semibold mb-4">Spend by Period</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={spendByPeriod}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
              <Tooltip formatter={v => [formatCurrency(v), 'Spend']} />
              <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} dot />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Category spend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border shadow-sm p-5">
          <h3 className="text-sm font-semibold mb-4">Spend by Category</h3>
          {(dashData?.spend_by_category || []).filter(c => c.amount > 0).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-slate-400">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(dashData?.spend_by_category || []).filter(c => c.amount > 0).slice(0, 8)} layout="vertical" margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 10 }} width={110} />
                <Tooltip formatter={v => [formatCurrency(v), 'Amount']} />
                <Bar dataKey="amount" fill="#10b981" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top merchants */}
        <div className="bg-white rounded-lg border shadow-sm p-5">
          <h3 className="text-sm font-semibold mb-4">Top Merchants</h3>
          {(dashData?.top_merchants || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-slate-400">No data</div>
          ) : (
            <div className="space-y-2">
              {(dashData?.top_merchants || []).slice(0, 10).map(({ merchant, amount }, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="w-4 text-slate-400 tabular-nums">{i+1}</span>
                  <span className="flex-1 truncate font-medium">{merchant}</span>
                  <span className="tabular-nums font-semibold">{formatCurrency(amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
